

import { TierChip } from './TierChip';

import { formatHotAge, Cell, formatCapacitySummary, buildLeadSummary, CopyButton, formatDate } from './triageDeskShared';
import { useTriageController } from './useTriageController';
import { verifyActionFor } from './triageVerifyOutcome';
export function TriageQueueRows({ ctx }: { ctx: ReturnType<typeof useTriageController> }) {
const { tab, loading, setError, setIrrelevantLead, irrelevantPendingId, irrelevantOriginQueryRef, irrelevantOriginDatasetRevisionRef, nameEdit, setNameEdit, nameSaving, setResponsesOpenFor, triageQueryIdentity, appliedDatasetRevisionRef, openPicker, openVerify, saveName, displayRows } = ctx;
return (<><tbody>
            {loading && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100 animate-pulse">
                    {Array.from({ length: 18 }).map((__, j) => (
                      <td key={j} className="px-3 py-3">
                        <div className="h-3 bg-gray-100 rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}

            {!loading && displayRows.length === 0 && (
              <tr>
                <td colSpan={20} className="px-3 py-12 text-center text-sm text-gray-500">
                  {tab === 'unassigned' && (
                    <>
                      All caught up. Ananya will surface new leads here within 5 min.<br />
                      <span className="text-xs">Last sync: just now.</span>
                    </>
                  )}
                  {tab === 'assigned' && 'No leads have been assigned yet.'}
                  {tab === 'self_made' &&
                    'Self-made leads appear when reps add them from the AURA Sales app.'}
                  {tab === 'b2b' && 'No B2B leads found. Adjust the filters above or check back later.'}
                  {tab === 'hot_overdue' && 'No overdue HOT leads right now.'}
                </td>
              </tr>
            )}

            {!loading && displayRows.map((r, idx) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                  {formatDate(r.createdAt)}
                  {tab === 'hot_overdue' && (
                    <span
                      className="ml-1.5 text-xs font-medium text-red-700"
                      title={`HOT since ${new Date(r.createdAt).toLocaleString()}`}
                    >
                      · waiting {formatHotAge(r.createdAt)}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        r.verifiedStatus === 'verified' ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={r.verifiedStatus === 'verified' ? 'Verified by Ananya' : 'Unverified'}
                    />
                    <Cell value={r.source} empty="dash" />
                  </div>
                </td>
                <td className="px-3 py-2">
                  {nameEdit?.id === r.id ? (
                    <span className="inline-flex items-center gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={nameEdit.value}
                        disabled={nameSaving}
                        onChange={(e) => setNameEdit({ id: r.id, value: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveName();
                          if (e.key === 'Escape') setNameEdit(null);
                        }}
                        className="w-36 px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        placeholder="Customer name"
                      />
                      <button
                        onClick={saveName}
                        disabled={nameSaving}
                        className="text-green-700 hover:bg-green-50 rounded px-1.5 py-1 text-sm font-bold disabled:opacity-50"
                        title="Save name (Enter)"
                      >✓</button>
                      <button
                        onClick={() => setNameEdit(null)}
                        disabled={nameSaving}
                        className="text-gray-400 hover:text-gray-700 rounded px-1.5 py-1 text-sm disabled:opacity-50"
                        title="Cancel (Esc)"
                      >×</button>
                    </span>
                  ) : (
                    <span className="group/name inline-flex items-center gap-1.5">
                      <Cell value={r.name} onOpen={() => openVerify(r)} />
                      <button
                        onClick={() => setNameEdit({ id: r.id, value: r.name ?? '' })}
                        className="opacity-0 group-hover/name:opacity-100 focus:opacity-100 text-gray-400 hover:text-blue-700 transition-opacity"
                        title="Edit lead/contact name"
                        aria-label={`Edit name for ${r.name ?? r.phone ?? 'lead'}`}
                      >✎</button>
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-800">{r.phone || <Cell value={null} empty="dash" />}</td>
                <td className="px-3 py-2"><Cell value={[r.location, r.state].filter(Boolean).join(', ') || null} onOpen={() => openVerify(r)} /></td>
                <td className="px-3 py-2"><Cell value={r.requirement} onOpen={() => openVerify(r)} /></td>
                <td className="px-3 py-2"><Cell value={r.applicationType} onOpen={() => openVerify(r)} /></td>
                <td className="px-3 py-2"><Cell value={r.businessSegment} onOpen={() => openVerify(r)} /></td>
                {/* ── Avika (2026-06-10) cells ─────────────────────────────── */}
                <td className="px-3 py-2">
                  {r.waLineLabel ? (
                    <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
                      {r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : r.waLineLabel}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><Cell value={formatCapacitySummary(r.capacity, r.quantity) || null} onOpen={() => openVerify(r)} /></td>
                <td className="px-3 py-2"><Cell value={r.timeline} onOpen={() => openVerify(r)} /></td>
                <td className="px-3 py-2">
                  {r.priceShared ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      {r.priceRange || 'Yes'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">No</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.score == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className="font-semibold text-gray-900">{r.score}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <TierChip tier={r.tier} isB2bPriority={r.isB2bPriority} isOutOfBudget={r.isOutOfBudget} />
                </td>
                <td className="px-3 py-2">
                  {r.verifiedBy === 'avika' || r.score != null ? (
                    <button
                      onClick={() => setResponsesOpenFor(r)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                      title="View the full WhatsApp Q&A + score breakdown"
                    >
                      Responses
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><CopyButton text={buildLeadSummary(r)} /></td>
<td className="px-3 py-2 whitespace-nowrap">{formatHotAge(r.createdAt)}</td>
                <td className="px-3 py-2">
                  {/* 2026-10-09 — every row gets an action (any tab): Verify,
                      Re-check details (answers missing) or Re-check. BUG 1b:
                      badge ABOVE the action, never beside it — side by side the
                      cell grew wider than a plain Verify cell and the action
                      sat past the table's visible edge on the Unassigned tab.
                      A source with incomplete details (Avika chat with few
                      answers, a half-filled manual save) shows an amber
                      "· incomplete" badge, never a verified-looking one. The
                      Overdue HOT feed has no verification snapshot: no badge. */}
                  <div className="flex flex-col items-start gap-1 whitespace-nowrap" data-verify-cell>
                  {tab === 'hot_overdue' ? null : r.verifiedStatus !== 'verified' && r.verifiedBy ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 px-2 py-0.5 rounded"
                        title="Started, but the required details are not complete"
                      >
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                        {r.verifiedBy === 'avika' ? 'Avika' : r.verifiedBy === 'chatkart' ? 'ChatKart' : `Manual${r.verifiedByName ? ` · ${r.verifiedByName}` : ''}`} · incomplete
                      </span>
                  ) : r.verifiedStatus === 'verified' ? (
                    r.verifiedBy === 'avika' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded"
                        title="Qualified by Avika (ChatKart line 2)"
                      >
                        <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
                        Avika
                      </span>
                    ) : r.verifiedBy === 'manual' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded"
                        title={`Manually verified${r.verifiedByName ? ' by ' + r.verifiedByName : ''}`}
                      >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        Manual{r.verifiedByName ? ` · ${r.verifiedByName}` : ''}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded"
                        title="Verified by Ananya (ChatKart)"
                      >
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        ChatKart
                      </span>
                    )
                  ) : null}
                  {(() => {
                    const action = verifyActionFor(r, tab);
                    if (action === 'verify') return (
                      <button
                        onClick={() => openVerify(r)}
                        className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                        title="Call the customer and record their answers"
                      >
                        Verify
                      </button>
                    );
                    if (action === 'recheck_incomplete') return (
                      <button
                        onClick={() => openVerify(r)}
                        className="text-xs font-medium text-amber-800 hover:text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-300 hover:border-amber-500 rounded px-2 py-0.5 transition-colors"
                        title="Required details are missing — call the customer and fill them in"
                      >
                        Re-check details
                      </button>
                    );
                    return (
                      <button
                        onClick={() => openVerify(r)}
                        className="text-xs text-gray-500 hover:text-blue-700 hover:bg-blue-50 border border-gray-200 hover:border-blue-400 rounded px-1.5 py-0.5 transition-colors"
                        title="Open the details and correct them"
                      >
                        Re-check
                      </button>
                    );
                  })()}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {tab === 'unassigned' ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPicker(r, 'assign')}
                        disabled={irrelevantPendingId === r.id}
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Assign to ▾
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError(null);
                          irrelevantOriginQueryRef.current = triageQueryIdentity;
                          irrelevantOriginDatasetRevisionRef.current = appliedDatasetRevisionRef.current;
                          setIrrelevantLead(r);
                        }}
                        disabled={irrelevantPendingId === r.id}
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap"
                      >
                        Mark as irrelevant
                      </button>
                    </div>
                  ) : tab === 'hot_overdue' || (tab === 'b2b' && !r.assignedToName) ? (
                    // L138 — the searchable member picker replaced the team
                    // dropdown (every row assigns member-wise now).
                    // B2B (2026-08-13, Manak): unowned B2B rows assign directly;
                    // owned B2B rows fall to the locked+Reassign branch below.
                    <button
                      type="button"
                      onClick={() => openPicker(r, 'assign')}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      Assign to ▾
                    </button>
                  ) : tab === 'assigned' || (tab === 'b2b' && !!r.assignedToName) ? (
                    // L142 — assigned rows are LOCKED: chip + explicit Reassign
                    // (no live picker until Reassign is tapped). Owned B2B rows
                    // reuse the same locked+Reassign affordance.
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-800 bg-gray-100 border border-gray-200 rounded px-2 py-1 whitespace-nowrap"
                        title="Locked — use Reassign to change the owner"
                      >
                        <span aria-hidden>🔒</span>
                        {[r.assignedToName, r.coordinatorName].filter(Boolean).join(' + ') || '—'}
                        {r.teamName ? <span className="text-gray-500"> · {r.teamName}</span> : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => openPicker(r, 'reassign')}
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        Reassign
                      </button>
                    </div>
                  ) : (
                    // Self-made: the header is "Created By" — who made the lead, not its owner.
                    <Cell value={r.createdByName ?? null} empty="dash" />
                  )}
                  {r.assignedDealerName ? (
                    // L238 — fulfilling dealer, shown alongside (never instead of) the owner.
                    <span
                      data-testid={`dealer-chip-${r.id}`}
                      className="mt-1 inline-flex items-center text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 whitespace-nowrap"
                      title="Fulfilling dealer (in addition to the rep)"
                    >
                      {`Dealer: ${r.assignedDealerName}`}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody></>);
}
